/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React from "react";
import Avatar from "react-avatar";
import { useNavigate } from "react-router-dom";
import PlanUnsubscribe from "../subscription/files/PlanUnsubscribe";

interface UserProfile {
  name: string;
  profile?: string;
}

interface Plan {
  name: string;
  amount: number;
  originalAmount: number;
  validityCount: string;
  validity: number;
  is_trial: boolean;
  isUnsubscriped: boolean;
  plan_start_date: string;
  plan_end_date: string;
  plantype?: {
    planTypeName: string;
  };
}

interface HeaderProps {
  userProfile?: UserProfile | null;
  walletBalance?: number;
  subscribedActivePlan?: Plan;
}

const Header: React.FC<HeaderProps> = ({
  userProfile,
  walletBalance,
  subscribedActivePlan,
}) => {
  const navigate = useNavigate();

  const planType = subscribedActivePlan?.plantype?.planTypeName;
  const planAmount = subscribedActivePlan?.originalAmount;

  return (
    <section className="head container-fluid px-3 py-2">
      {/* Main Header Row */}
      <div className="row align-items-center g-2">
        {/* Logo and Welcome Message */}
        <div className="col-8 col-md-6 d-flex align-items-center">
          {/* Logo - Smaller on mobile */}
          <div className="nav-brand me-2 me-md-3">
            <img
              className="img-fluid"
              src="/assets/images/logo.svg"
              alt="Logo"
              style={{ height: "40px" }} // Reduced from 70px
            />
          </div>
          
          {/* Welcome Message - Smaller text on mobile */}
          <h1 className="fs-6 fw-bold text-success mb-0 d-none d-sm-block">
            Welcome to Plan Subscription
          </h1>
          <h1 className="fs-7 fw-bold text-success mb-0 d-block d-sm-none">
            Plans
          </h1>
        </div>

        {/* Plan Name and Profile - Right-aligned */}
        <div className="col-4 col-md-6 d-flex justify-content-end align-items-center">
          {/* Plan Name Badge - Only show if active plan exists */}
          {subscribedActivePlan && (
            <div className="d-none d-md-block me-md-3"> {/* Hide on mobile */}
              <span
                className="badge bg-primary"
                style={{ 
                  fontSize: "0.8rem", 
                  padding: "6px 12px" // Smaller padding
                }}
              >
                {subscribedActivePlan.name}
                {planType !== "Free Plan" && subscribedActivePlan.validity &&
                  ` (${subscribedActivePlan.is_trial ? "Trial" : subscribedActivePlan.validity})`
                }
              </span>
            </div>
          )}

          {/* Profile Dropdown */}
          {userProfile && (
            <div className="dropdown profile-dd">
              <button
                className="btn dropdown-toggle d-flex align-items-center p-1 p-md-2" // Smaller padding on mobile
                type="button"
                id="dropdownMenuButton1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {/* Avatar - Smaller on mobile */}
                {userProfile.profile ? (
                  <Avatar
                    src={userProfile.profile}
                    name={userProfile.name}
                    maxInitials={1}
                    size="30"
                    round
                  />
                ) : (
                  <Avatar
                    name={userProfile.name}
                    maxInitials={1}
                    size="30"
                    round
                    color="#607458"
                    fgColor="#ffffff"
                  />
                )}
                {/* Name - Hide on mobile */}
                <span className="ps-2 mb-0 d-none d-md-inline-block">
                  {userProfile.name}
                </span>
              </button>
              
              {/* Dropdown Menu */}
              <ul
                className="dropdown-menu dropdown-menu-end" // Align to right
                aria-labelledby="dropdownMenuButton1"
              >
                <li className="dropdown-item">
                  <strong>Wallet Balance:</strong> $
                  {walletBalance?.toFixed(2) || "0.00"}
                </li>
                
                <li className="dropdown-item mt-2">
                  <strong>Plan:</strong>{" "}
                  {subscribedActivePlan?.name || "No Active Plan"}{" "}
                  {planType !== "Free Plan" && subscribedActivePlan?.validity &&
                    ` (${subscribedActivePlan.is_trial ? "Trial" : subscribedActivePlan.validity})`
                  }
                </li>
                
                {planType !== "Free Plan" && 
                 planAmount !== 0 && 
                 !subscribedActivePlan?.is_trial && 
                 !subscribedActivePlan?.isUnsubscriped && (
                  <li className="dropdown-item mt-2">
                    <PlanUnsubscribe />
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Plan Name - Mobile Only (below main header) */}
      {subscribedActivePlan && (
        <div className="row mt-2 d-block d-md-none"> {/* Show only on mobile */}
          <div className="col-12 text-center">
            <span
              className="badge bg-primary"
              style={{ 
                fontSize: "0.8rem", 
                padding: "6px 12px",
                width: "100%"
              }}
            >
              {subscribedActivePlan.name}
              {planType !== "Free Plan" && subscribedActivePlan.validity &&
                ` (${subscribedActivePlan.is_trial ? "Trial" : subscribedActivePlan.validity})`
              }
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

export default Header;